import React, { useState } from 'react';
import { ScenarioConfig, UserProfile, Gender } from '../types';
import { Button } from './Button';

interface ScenarioProps {
  user: UserProfile;
  onStart: (scenario: ScenarioConfig) => void;
}

export const ScenarioStep: React.FC<ScenarioProps> = ({ user, onStart }) => {
  const [context, setContext] = useState('');
  const [relationshipStatus, setRelationshipStatus] = useState<ScenarioConfig['relationshipStatus']>('Strangers');
  const [difficulty, setDifficulty] = useState<ScenarioConfig['difficulty']>('Medium');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onStart({ context, relationshipStatus, difficulty });
  };

  const suggestions = [
    "She replies with one word answers.",
    "He ghosted me for two days and just texted 'hey'.",
    "We matched on an app but the convo is dying.",
    "Long distance - we are running out of things to say."
  ];

  return (
    <div className="h-full overflow-y-auto max-w-md mx-auto p-6 animate-fade-in pb-32">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Set the Scene</h2>
        <p className="text-slate-500 text-sm">Describe the situation you want to master.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-slate-700">Current Status</label>
          <div className="grid grid-cols-2 gap-2">
            {['Strangers', 'Dating', 'Long Distance', 'Complicated'].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setRelationshipStatus(status as any)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  relationshipStatus === status 
                    ? 'bg-brand-50 border-brand-500 text-brand-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-semibold text-slate-700">What's the specific challenge?</label>
          <div className="relative">
            <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-700 bg-slate-800 text-white placeholder-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none min-h-[100px] text-sm shadow-sm"
                placeholder={`e.g., The ${user.targetGender === Gender.Female ? 'girl' : 'guy'} I like seems uninterested...`}
                required
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button 
                key={i}
                type="button"
                onClick={() => setContext(s)}
                className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md hover:bg-slate-200 border border-slate-200"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-semibold text-slate-700">Difficulty Level</label>
          <div className="flex items-center justify-between bg-slate-100 p-1 rounded-xl border border-slate-200">
            {['Easy', 'Medium', 'Hard'].map((lvl) => (
              <button
                key={lvl}
                type="button"
                onClick={() => setDifficulty(lvl as any)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                  difficulty === lvl 
                    ? 'bg-white text-slate-900 shadow-sm border border-slate-100' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {lvl}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 px-1">
            {difficulty === 'Easy' && "Partner is talkative and forgiving."}
            {difficulty === 'Medium' && "Realistic responses with average interest."}
            {difficulty === 'Hard' && "Partner is cold, dry, or hard to impress."}
          </p>
        </div>

        <div className="pt-4">
          <Button type="submit" fullWidth disabled={!context}>
            Start Simulation
          </Button>
        </div>
      </form>
    </div>
  );
};